export const RECEIPT_SCHEMA_VERSION = "sharemyai.device-receipt.v1";
export const FLUX_THRESHOLDS = Object.freeze({
  storageBindingBytes: 128 * 1024 * 1024,
  maxBufferBytes: 256 * 1024 * 1024
});

export function classifyDevice({ capability, benchmark, modelProbe }) {
  if (!capability?.webgpuAvailable) {
    return {
      tier: "unsupported",
      title: "WebGPU unavailable",
      detail: capability?.reason || "This browser did not expose a WebGPU adapter.",
      tone: "error"
    };
  }

  if (capability.deviceError || !benchmark?.valid) {
    return {
      tier: "blocked",
      title: "Benchmark incomplete",
      detail: capability.deviceError || benchmark?.error || "The compute proxy did not finish reliably.",
      tone: "error"
    };
  }

  const limits = capability.limits || {};
  const memoryGate = limits.maxStorageBufferBindingSize >= FLUX_THRESHOLDS.storageBindingBytes
    && limits.maxBufferSize >= FLUX_THRESHOLDS.maxBufferBytes;

  if (!capability.features?.includes("shader-f16") || !memoryGate) {
    return {
      tier: "baseline",
      title: "WebGPU baseline",
      detail: "Useful browser compute is available, but this adapter misses one or more Klein Q4 candidate gates.",
      tone: "warning"
    };
  }

  if (modelProbe?.canonicalRender?.success) {
    return {
      tier: "qualified",
      title: "Render qualified",
      detail: "This device completed the canonical model render and may enter a verified work pool.",
      tone: "success"
    };
  }

  return {
    tier: "candidate",
    title: "Klein Q4 candidate",
    detail: modelProbe?.runtimeAvailable
      ? "Hardware gates passed. Run the canonical render to qualify this device."
      : "Hardware gates passed. Qualification awaits the audited model runtime.",
    tone: "success"
  };
}

export function createReceipt({ capability, benchmark, modelProbe, events, userAgent, secureContext }) {
  const createdAt = new Date().toISOString();
  const qualification = classifyDevice({ capability, benchmark, modelProbe });
  const entropy = `${createdAt}:${benchmark?.gflops || 0}:${capability?.adapter?.description || "unknown"}`;
  const id = simpleHash(entropy);

  return {
    schemaVersion: RECEIPT_SCHEMA_VERSION,
    id: `sm-${id}`,
    createdAt,
    environment: { userAgent, secureContext },
    capability,
    benchmark,
    modelProbe,
    qualification,
    events
  };
}

export function serializeReceipt(receipt) {
  return `${JSON.stringify(receipt, null, 2)}\n`;
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GiB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MiB`;
  return `${Math.round(bytes / 1024)} KiB`;
}

function simpleHash(input) {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
